import * as React from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Chip,
  Paper,
  Stack,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Fade,
  CircularProgress,
  IconButton,
} from "@mui/material";
import HeartInsufficientDialog from "../../../components/HeartInsufficientDialog";
import FavoriteIcon from "@mui/icons-material/Favorite";
import SendIcon from "@mui/icons-material/Send";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { Close, Person, LocationOn, Favorite, Flag, MoreVert } from "@mui/icons-material";
import { Menu, MenuItem, ListItemIcon, ListItemText } from "@mui/material";
import type { CupidCard, CupidCardDetail } from "../types/dashboard";
import { getCardDetail } from "../api/cardsApi";
import {
  getCardDisplayStatus,
  canShowExtendButton,
  formatCountdown,
  formatRemainingTime,
  msUntilExpiry,
  msUntilExtendDeadline,
} from "../../../utils/cardStatus";

type Props = {
  card: CupidCard;
  walletHearts: number;
  onOpen: (cardId: string) => void;
  onExtend: (cardId: string) => void;
  onChat: (card: CupidCard) => void;
};

export default React.memo(function CupidCardItem({
  card,
  walletHearts,
  onOpen,
  onExtend,
  onChat,
}: Props) {
  const navigate = useNavigate();
  const [confirmDialog, setConfirmDialog] = React.useState(false);
  const [isOpening, setIsOpening] = React.useState(false);
  const [heartModal, setHeartModal] = React.useState<{ open: boolean; required: number; label: string }>({
    open: false, required: 0, label: '',
  });
  const [profileModal, setProfileModal] = React.useState(false);
  const [cardDetail, setCardDetail] = React.useState<CupidCardDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = React.useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = React.useState(0);
  const [detailError, setDetailError] = React.useState<string | null>(null);
  const [reportMenuAnchor, setReportMenuAnchor] = React.useState<null | HTMLElement>(null);
  const [now, setNow] = React.useState(() => new Date());

  React.useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  const displayStatus = getCardDisplayStatus(card, now);
  const isLocked = displayStatus === "LOCKED";
  const openDisabled = walletHearts < card.openCostHearts;
  const expiryMs = msUntilExpiry(card.expiresAt, now);
  const extendDeadlineMs = msUntilExtendDeadline(card.extendDeadlineAt, now);
  const isExpiringSoon = (displayStatus === "LOCKED" || displayStatus === "OPENED") && expiryMs > 0 && expiryMs < 24 * 60 * 60 * 1000;
  // Task #5 Fix: EXPIRED_EXTENDABLE 상태에서는 isExtendable 서버 값과 무관하게 extendCount 기준으로 버튼 표시
  const showExtendButton = displayStatus === "EXPIRED_EXTENDABLE"
    ? card.extendCount < 1
    : canShowExtendButton(card, now);

  // 4단계 색상: <6h→error, <24h→warning, >=24h→default
  const badgeColor = displayStatus === "EXPIRED_EXTENDABLE"
    ? "error"
    : expiryMs > 0 && expiryMs < 6 * 3600 * 1000
    ? "error"
    : expiryMs > 0 && expiryMs < 24 * 3600 * 1000
    ? "warning"
    : "default";
  const badgeLabel = displayStatus === "EXPIRED_EXTENDABLE"
    ? `마감 ${formatCountdown(extendDeadlineMs)}`
    : formatRemainingTime(expiryMs);

  const imageSrc = card.thumbnailImage.photoUrl;

  const handleOpenClick = () => {
    if (walletHearts < card.openCostHearts) {
      setHeartModal({ open: true, required: card.openCostHearts, label: '카드 오픈' });
      return;
    }
    setConfirmDialog(true);
  };

  const handleConfirmOpen = async () => {
    setConfirmDialog(false);
    setIsOpening(true);

    // 애니메이션 효과를 위한 딜레이
    setTimeout(async () => {
      try {
        await onOpen(card.cardId);
      } catch (error: any) {
        // race condition 시 INSUFFICIENT_HEARTS 에러 처리
        if (error?.errorType === 'INSUFFICIENT_HEARTS' || error?.errorCode === 'GTH_4020') {
          setHeartModal({ open: true, required: card.openCostHearts, label: '카드 오픈' });
        }
      } finally {
        setIsOpening(false);
      }
    }, 500);
  };

  const handleProfileClick = async () => {
    // 모달 먼저 열기 (즉시 피드백)
    setProfileModal(true);
    setDetailError(null);
    setCardDetail(null);
    setSelectedImageIndex(0);
    setLoadingDetail(true);
    
    try {
      const detail = await getCardDetail(card.cardId);
      setCardDetail(detail);
      setDetailError(null);
    } catch (error: any) {
      setDetailError(error?.message || "카드 상세 정보를 불러올 수 없습니다.");
      setCardDetail(null);
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleExtendClick = () => {
    if (walletHearts < (card.extendCostHearts ?? 3)) {
      setHeartModal({ open: true, required: card.extendCostHearts ?? 3, label: '카드 연장' });
      return;
    }
    onExtend(card.cardId);
  };

  return (
    <>
    <Paper
      variant="outlined"
      sx={{
        borderRadius: '16px',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        position: 'relative',
        bgcolor: displayStatus === 'LOCKED' ? '#F0F3F8' : '#FFFFFF',
        ...(displayStatus === 'OPENED' && {
          borderLeft: '3px solid #C9A86C',
          boxShadow: '0 2px 16px rgba(13,31,60,0.07)',
        }),
        ...(displayStatus === 'EXPIRED_EXTENDABLE' && {
          border: '1.5px solid #FF6B35',
          borderLeft: '3px solid #FF6B35',
        }),
        ...(card.isMutual && displayStatus !== 'EXPIRED_EXTENDABLE' && {
          border: '2px solid #F5A623',
          boxShadow: '0 2px 16px rgba(245,166,35,0.18)',
        }),
        transform: isOpening ? 'scale(1.03)' : 'scale(1)',
        transition: 'all 0.5s ease-in-out',
        opacity: isOpening ? 0.8 : 1,
        '&:hover': {
          boxShadow: '0 8px 32px rgba(13,31,60,0.14)',
          transform: isOpening ? 'scale(1.03)' : 'translateY(-2px)',
        },
      }}
    >
      {/* isOpening 오버레이 — 네이비/골드 */}
      {isOpening && (
        <Box
          sx={{
            position: 'absolute', inset: 0, zIndex: 2,
            background: 'linear-gradient(45deg, rgba(13,31,60,0.12), rgba(201,168,108,0.12))',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <Typography sx={{ color: '#0D1F3C', fontWeight: 800, fontSize: '1rem' }}>
            카드를 여는 중...
          </Typography>
        </Box>
      )}

      {/* EXPIRED: 긴급 배너 */}
      {displayStatus === 'EXPIRED_EXTENDABLE' && (
        <Box sx={{
          bgcolor: '#FF6B35', color: 'white',
          fontSize: '12px', fontWeight: 700,
          py: '7px', px: '18px', letterSpacing: '0.2px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <span>지금 연장하세요</span>
          {card.isMutual && (
            <Box sx={{
              bgcolor: '#FFFFFF', color: '#F5A623',
              borderRadius: '8px', px: '9px', py: '3px',
              fontSize: '11px', fontWeight: 800,
              ml: '10px', flexShrink: 0,
            }}>
              매칭 확률 높음🔥
            </Box>
          )}
        </Box>
      )}

      {/* 상호 카드 뱃지 — 좌상단 절대 위치 */}
      {card.isMutual && displayStatus !== 'EXPIRED_EXTENDABLE' && (
        <Box sx={{
          position: 'absolute', top: 12, left: 12, zIndex: 5,
          '@keyframes mutualPulse': {
            '0%': { transform: 'scale(1)' },
            '50%': { transform: 'scale(1.15)' },
            '100%': { transform: 'scale(1)' },
          },
          animation: 'mutualPulse 600ms ease-out 1',
          bgcolor: '#F5A623', color: 'white',
          borderRadius: '10px', px: '10px', py: '5px',
          fontSize: '12px', fontWeight: 800,
          letterSpacing: '0.1px',
          boxShadow: '0 2px 8px rgba(245,166,35,0.45)',
          pointerEvents: 'none',
        }}>
          매칭 확률 높음🔥
        </Box>
      )}

      {isLocked ? (
        /* ── LOCKED: 대형 블러 초상화 레이아웃 ── */
        <>
          {/* 1. Top bar — 이미지 위 별도 영역 */}
          <Box sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
            p: '14px 16px 10px', gap: '8px',
          }}>
              {/* 타이머 칩 */}
              <Box sx={{
                bgcolor: badgeColor === 'error' ? '#FFEBEE' : '#FFF3E0',
                color: badgeColor === 'error' ? '#C62828' : '#E65100',
                fontSize: 12, fontWeight: 700,
                px: '11px', py: '6px', borderRadius: '9px',
              }}>
                ⏰ {badgeLabel}
              </Box>
              {/* 잠금 배지 */}
              <Box sx={{
                width: 34, height: 34, borderRadius: '9px',
                bgcolor: '#0D1F3C',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 17, flexShrink: 0,
              }}>
                🔒
              </Box>
          </Box>

          {/* 2. 블러 이미지 영역 */}
          <Box sx={{ position: 'relative', width: '100%', height: 300, overflow: 'hidden', flexShrink: 0 }}>
            {imageSrc ? (
              <Box
                component="img"
                src={imageSrc}
                alt="프로필"
                sx={{
                  width: '100%', height: '100%', objectFit: 'cover',
                }}
              />
            ) : (
              <Box sx={{
                width: '100%', height: '100%',
                background: 'linear-gradient(135deg, #EDD5C0 0%, #C8A882 45%, #8A6050 100%)',
                filter: 'blur(12px)', transform: 'scale(1.1)',
              }} />
            )}
            {/* 하단 그라디언트 페이드 */}
            <Box sx={{
              position: 'absolute', bottom: 0, left: 0, right: 0, height: 110,
              background: 'linear-gradient(to bottom, transparent 40%, rgba(13,31,60,0.75) 100%)',
              pointerEvents: 'none',
            }} />
          </Box>

          {/* 3. Info block */}
          <Box sx={{ p: '14px 16px 0' }}>
            <Typography sx={{
              fontSize: 20, fontWeight: 800, color: '#1A1A1A',
              letterSpacing: '-0.3px', mb: '5px',
            }}>
              ○○○ 님 · {card.cardPreview.age}세
            </Typography>
            {card.cardPreview.residenceRegion && (
              <Typography sx={{ fontSize: 15, color: '#666666' }}>
                📍 {card.cardPreview.residenceRegion.level1Name} {card.cardPreview.residenceRegion.level2Name}
              </Typography>
            )}
          </Box>

          {/* 4. CTA block */}
          <Box sx={{ p: '14px 16px 18px', mt: 'auto' }}>
            {/* mutual + hasChat: 대화 우선 안내 배너 */}
            {card.isMutual && card.cardPreview.hasChat && (
              <Box sx={{
                bgcolor: '#FFF8E7', border: '1px solid #F5A623',
                borderRadius: '10px', p: '12px 14px', mb: '12px',
              }}>
                <Typography sx={{ fontSize: 14, color: '#7A5500', fontWeight: 600, lineHeight: 1.5 }}>
                  💬 이분이 먼저 대화를 시작했어요. 카드를 열면 프로필을 확인할 수 있어요.
                </Typography>
              </Box>
            )}

            {card.isMutual && card.cardPreview.hasChat ? (
              /* mutual + hasChat: 카드 열기 + 대화만 하기 */
              <Stack spacing={1.5}>
                <Button
                  variant="contained"
                  onClick={handleOpenClick}
                  fullWidth
                  sx={{
                    height: 52, borderRadius: '13px',
                    background: openDisabled ? undefined : 'linear-gradient(135deg, #C9A86C 0%, #E8C97A 100%)',
                    bgcolor: openDisabled ? '#C8B8A8' : undefined,
                    color: openDisabled ? 'white' : '#0D1F3C',
                    fontWeight: 800, fontSize: 17,
                    boxShadow: 'none',
                    '&:hover': {
                      background: openDisabled ? undefined : 'linear-gradient(135deg, #B8976B 0%, #D4B86A 100%)',
                      bgcolor: openDisabled ? '#C8B8A8' : undefined,
                      boxShadow: 'none',
                    },
                    '&:disabled': { bgcolor: '#C8B8A8', color: 'white' },
                  }}
                >
                  카드 열기 💛 {card.openCostHearts}하트
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => onChat(card)}
                  fullWidth
                  sx={{
                    height: 48, borderRadius: '13px',
                    border: '2px solid #F5A623', color: '#7A5500',
                    fontWeight: 700, fontSize: 15,
                    '&:hover': { bgcolor: '#FFF8E7', borderColor: '#E8963A' },
                  }}
                >
                  대화만 하기 →
                </Button>
              </Stack>
            ) : (
              /* 일반 / mutual(chat 없음): 단일 열기 버튼 */
              <>
                <Button
                  variant="contained"
                  onClick={handleOpenClick}
                  fullWidth
                  sx={{
                    height: 52, borderRadius: '13px',
                    background: openDisabled ? undefined : 'linear-gradient(135deg, #C9A86C 0%, #E8C97A 100%)',
                    bgcolor: openDisabled ? '#C8B8A8' : undefined,
                    color: openDisabled ? 'white' : '#0D1F3C',
                    fontWeight: 800, fontSize: 17,
                    boxShadow: 'none',
                    '&:hover': {
                      background: openDisabled ? undefined : 'linear-gradient(135deg, #B8976B 0%, #D4B86A 100%)',
                      bgcolor: openDisabled ? '#C8B8A8' : undefined,
                      boxShadow: 'none',
                    },
                    '&:disabled': { bgcolor: '#C8B8A8', color: 'white' },
                  }}
                >
                  열기 💛 {card.openCostHearts}하트
                </Button>
              </>
            )}
          </Box>
        </>
      ) : displayStatus === 'OPENED' ? (
        /* ── OPENED: A안 레이아웃 ── */
        <>
          {/* 1. Top bar — 이미지 위 별도 영역 */}
          <Box sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            p: '14px 16px 10px',
          }}>
            {/* 타이머 칩 */}
            <Box sx={{
              display: 'inline-flex', alignItems: 'center',
              bgcolor: '#FFF3E0', color: '#E65100',
              fontSize: '12px', fontWeight: 700,
              px: '11px', py: '6px', borderRadius: '9px',
            }}>
              ⏰ {badgeLabel}
            </Box>
            {/* 열람완료 배지 */}
            <Box sx={{
              display: 'inline-flex', alignItems: 'center',
              bgcolor: '#E8F5E9', color: '#2E7D32',
              fontSize: '12px', fontWeight: 700,
              px: '11px', py: '6px', borderRadius: '9px',
            }}>
              ✅ 열람완료
            </Box>
          </Box>

          {/* 2. 이미지 영역 — 300px, 그라디언트/배지 오버레이 없음 */}
          <Box sx={{ width: '100%', height: 300, overflow: 'hidden', flexShrink: 0 }}>
            {imageSrc ? (
              <Box
                component="img"
                src={imageSrc}
                alt="프로필"
                sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : (
              <Box sx={{ width: '100%', height: '100%', bgcolor: '#E8E0D8' }} />
            )}
          </Box>

          {/* 3. Info block — 이미지 아래 */}
          <Box sx={{ p: '14px 16px 0' }}>
            <Typography sx={{
              fontSize: '20px', fontWeight: 800, color: '#1A1A1A',
              letterSpacing: '-0.3px', mb: '5px',
            }}>
              {`${card.cardPreview.name ?? '익명'} 님`}
              {card.cardPreview.age ? ` · ${card.cardPreview.age}세` : ''}
            </Typography>
            {card.cardPreview.residenceRegion && (
              <Typography sx={{ fontSize: '15px', color: '#666666' }}>
                📍 {card.cardPreview.residenceRegion.level1Name} {card.cardPreview.residenceRegion.level2Name}
              </Typography>
            )}
          </Box>

          {/* 4. Interests block */}
          <Box sx={{ p: '12px 16px 0' }}>
            {card.cardPreview.interests && card.cardPreview.interests.length > 0 ? (
              <>
                <Typography sx={{
                  fontSize: '13px', fontWeight: 700, color: '#E8636A', mb: '9px',
                }}>
                  🎯 나와 공통 관심사 {card.cardPreview.interests.length}개
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '7px', alignItems: 'center' }}>
                  {card.cardPreview.interests.slice(0, 3).map(i => (
                    <Box
                      key={i.id}
                      sx={{
                        bgcolor: '#FFF0F1', color: '#E8636A',
                        border: '1px solid #FFD6D8', borderRadius: '20px',
                        px: '14px', py: '7px',
                        fontSize: '13px', fontWeight: 600,
                      }}
                    >
                      {i.name}
                    </Box>
                  ))}
                  {card.cardPreview.interests.length > 3 && (
                    <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#E8636A' }}>
                      +{card.cardPreview.interests.length - 3}개
                    </Typography>
                  )}
                </Box>
              </>
            ) : (
              <Typography sx={{ fontSize: '13px', color: '#8896AB' }}>
                관심사가 없어도 대화해보세요 💬
              </Typography>
            )}
          </Box>

          {/* 5. CTA block */}
          <Box sx={{ p: '14px 16px 18px', mt: 'auto' }}>
            <Button
              variant="contained"
              onClick={() => onChat(card)}
              fullWidth
              sx={{
                height: '52px', borderRadius: '13px',
                bgcolor: '#0D1F3C', color: 'white',
                fontSize: '17px', fontWeight: 800,
                '&:hover': { bgcolor: '#0A1830' },
              }}
            >
              대화 시작하기 →
            </Button>
            {showExtendButton && card.extendCostHearts != null && (
              <Button
                variant="outlined"
                onClick={handleExtendClick}
                fullWidth
                sx={{
                  height: '44px', mt: '9px', borderRadius: '13px',
                  border: '1.5px solid #E0E0E0', color: '#555555',
                  fontSize: '15px', fontWeight: 600,
                  '&:hover': { borderColor: '#C9A86C', bgcolor: '#FAF7F0' },
                }}
              >
                🔄 연장 ♥{card.extendCostHearts}
              </Button>
            )}
            <Button
              variant="outlined"
              onClick={handleProfileClick}
              fullWidth
              sx={{
                height: '44px', mt: '9px', borderRadius: '13px',
                border: '1.5px solid #E0E0E0', color: '#555555',
                fontSize: '15px', fontWeight: 600,
                '&:hover': { borderColor: '#0D1F3C' },
              }}
            >
              프로필 보기
            </Button>
          </Box>
        </>
      ) : (
        /* ── EXPIRED_EXTENDABLE: 큰 사진 레이아웃 (OPENED 포맷 동일) ── */
        <>
          {/* 1. Top bar */}
          <Box sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            p: '14px 16px 10px',
          }}>
            <Box sx={{
              display: 'inline-flex', alignItems: 'center',
              bgcolor: '#FFF3E0', color: '#E65100',
              fontSize: '12px', fontWeight: 700,
              px: '11px', py: '6px', borderRadius: '9px',
            }}>
              ⏰ 마감 {formatCountdown(extendDeadlineMs)}
            </Box>
            <Box sx={{
              display: 'inline-flex', alignItems: 'center',
              bgcolor: '#FFF0EB', color: '#FF6B35',
              fontSize: '12px', fontWeight: 700,
              px: '11px', py: '6px', borderRadius: '9px',
              border: '1px solid #FF6B35',
            }}>
              ⚠️ 만료됨
            </Box>
          </Box>

          {/* 2. 이미지 영역 — 300px */}
          <Box sx={{ width: '100%', height: 300, overflow: 'hidden', flexShrink: 0 }}>
            {imageSrc ? (
              <Box
                component="img"
                src={imageSrc}
                alt="프로필"
                sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : (
              <Box sx={{ width: '100%', height: '100%', background: 'linear-gradient(180deg, #F2D5C0 0%, #D9A882 50%, #B07050 100%)' }} />
            )}
          </Box>

          {/* 3. Info block */}
          <Box sx={{ p: '14px 16px 0' }}>
            <Typography sx={{
              fontSize: '20px', fontWeight: 800, color: '#1A1A1A',
              letterSpacing: '-0.3px', mb: '5px',
            }}>
              {`${card.cardPreview.name ?? '익명'} 님`}
              {card.cardPreview.age ? ` · ${card.cardPreview.age}세` : ''}
            </Typography>
            {card.cardPreview.residenceRegion && (
              <Typography sx={{ fontSize: '15px', color: '#666666' }}>
                📍 {card.cardPreview.residenceRegion.level1Name} {card.cardPreview.residenceRegion.level2Name}
              </Typography>
            )}
          </Box>

          {/* 4. CTA block */}
          <Box sx={{ p: '14px 16px 18px', mt: 'auto' }}>
            <Button
              variant="contained"
              startIcon={<AccessTimeIcon />}
              onClick={handleExtendClick}
              fullWidth
              sx={{
                height: '52px', borderRadius: '13px',
                bgcolor: '#FF6B35', color: 'white',
                fontSize: '17px', fontWeight: 800,
                '&:hover': { bgcolor: '#E55B2A' },
              }}
            >
              연장하기 ♥{card.extendCostHearts ?? 3}
            </Button>
            {card.status === 'OPENED' && (
              <Button
                variant="outlined"
                onClick={handleProfileClick}
                fullWidth
                sx={{
                  height: '44px', mt: '9px', borderRadius: '13px',
                  border: '1.5px solid #E0E0E0', color: '#555555',
                  fontSize: '15px', fontWeight: 600,
                  '&:hover': { borderColor: '#0D1F3C' },
                }}
              >
                프로필 보기
              </Button>
            )}
          </Box>
        </>
      )}
    </Paper>

    {/* 확인 다이얼로그 */}
    <Dialog 
      open={confirmDialog} 
      onClose={() => setConfirmDialog(false)}
      TransitionComponent={Fade}
      TransitionProps={{ timeout: 300 }}
    >
      <DialogTitle sx={{ fontFamily: '"Playfair Display", serif', fontWeight: 700, color: '#0D1F3C' }}>
        카드를 열어볼까요?
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1" gutterBottom>
          하트 {card.openCostHearts}개를 사용해서 상대방의 프로필을 확인하시겠어요?
        </Typography>
        <Typography variant="body2" color="text.secondary">
          현재 보유 하트: {walletHearts}개
        </Typography>
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button 
          onClick={() => setConfirmDialog(false)}
          variant="outlined"
          sx={{ borderRadius: 2 }}
        >
          취소
        </Button>
        <Button 
          onClick={handleConfirmOpen}
          variant="contained"
          startIcon={<FavoriteIcon />}
          sx={{ borderRadius: 2, fontWeight: 700, bgcolor: '#0D1F3C', '&:hover': { bgcolor: '#1A3461' } }}
        >
          카드 열기
        </Button>
      </DialogActions>
    </Dialog>

    {/* 하트 부족 모달 */}
    <HeartInsufficientDialog
      open={heartModal.open}
      onClose={() => setHeartModal(prev => ({ ...prev, open: false }))}
      onPurchase={() => {
        setHeartModal(prev => ({ ...prev, open: false }));
        navigate('/heart/purchase');
      }}
      requiredHearts={heartModal.required}
      currentBalance={walletHearts}
      actionLabel={heartModal.label}
    />

    {/* 프로필 모달 */}
    <Dialog 
      open={profileModal} 
      onClose={() => {
        setProfileModal(false);
        setCardDetail(null);
        setDetailError(null);
        setSelectedImageIndex(0);
      }}
      TransitionComponent={Fade}
      TransitionProps={{ timeout: 300 }}
      maxWidth="md"
      fullWidth
      fullScreen={window.innerWidth < 600}
      PaperProps={{
        sx: {
          borderRadius: { xs: 0, sm: 3 },
          maxHeight: { xs: '100vh', sm: '95vh' },
          m: { xs: 0, sm: 2 },
          display: 'flex',
          flexDirection: 'column',
        }
      }}
    >
      {/* 헤더 (모바일 전용) */}
      <Box
        sx={{
          display: { xs: 'flex', sm: 'none' },
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          position: 'sticky',
          top: 0,
          zIndex: 1,
          flexShrink: 0
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          프로필
        </Typography>
        <IconButton
          onClick={() => {
            setProfileModal(false);
            setCardDetail(null);
            setDetailError(null);
            setSelectedImageIndex(0);
          }}
          size="small"
        >
          <Close />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 0, overflow: 'auto', flex: 1, minHeight: 0 }}>
        {loadingDetail ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <CircularProgress size={48} />
            <Typography sx={{ mt: 2 }}>프로필 정보를 불러오는 중...</Typography>
          </Box>
        ) : cardDetail ? (
          <Box>
            {/* 프로필 이미지 갤러리 */}
            <Box sx={{ position: 'relative' }}>
              {/* 메인 이미지 */}
              <Box sx={{ position: 'relative' }}>
                <Box
                  component="img"
                  src={cardDetail.targetProfile.profilePhotos[selectedImageIndex]?.endpointUrl || cardDetail.thumbnailImage.photoUrl}
                  alt={`${cardDetail.targetProfile.name} 프로필`}
                  sx={{
                    width: '100%',
                    height: 'auto',
                    display: 'block',
                    maxHeight: { xs: 450, sm: 550, md: 600 },
                    objectFit: 'contain',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                  onClick={() => {
                    const nextIndex = (selectedImageIndex + 1) % Math.max(cardDetail.targetProfile.profilePhotos.length, 1);
                    setSelectedImageIndex(nextIndex);
                  }}
                />
                
                {/* 이미지 오버레이 (데스크톱 전용) */}
                <Box 
                  sx={{
                    display: { xs: 'none', sm: 'block' },
                    position: 'absolute',
                    top: 16,
                    right: 16,
                  }}
                >
                  <IconButton
                    onClick={() => {
                      setProfileModal(false);
                      setCardDetail(null);
                      setDetailError(null);
                      setSelectedImageIndex(0);
                    }}
                    sx={{
                      bgcolor: 'rgba(0,0,0,0.3)',
                      color: 'white',
                      '&:hover': { bgcolor: 'rgba(0,0,0,0.5)' }
                    }}
                  >
                    <Close />
                  </IconButton>
                </Box>

                {/* 기본 정보 오버레이 */}
                <Box 
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
                    p: { xs: 2, sm: 3 },
                  }}
                >
                  <Typography 
                    variant="h5"
                    sx={{ 
                      color: 'white', 
                      fontWeight: 800, 
                      mb: 0.5,
                      fontSize: { xs: '1.5rem', sm: '2rem' }
                    }}
                  >
                    {cardDetail.targetProfile.name}
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'white', opacity: 0.9, mb: 1 }}>
                    {cardDetail.targetProfile.age}세 · {cardDetail.targetProfile.residenceRegion.level1Name} {cardDetail.targetProfile.residenceRegion.level2Name}
                  </Typography>
                  {cardDetail.targetProfile.jobTitle && (
                    <Chip
                      label={cardDetail.targetProfile.jobTitle}
                      sx={{
                        bgcolor: '#C9A86C',
                        color: '#0D1F3C',
                        fontWeight: 700,
                        fontSize: '0.9rem'
                      }}
                    />
                  )}
                </Box>
              </Box>

              {/* 추가 프로필 이미지 (있는 경우) */}
              {cardDetail.targetProfile.profilePhotos.length > 1 && (
                <Box sx={{ 
                  display: 'flex', 
                  gap: 1, 
                  p: { xs: 2, sm: 3 },
                  overflowX: 'auto',
                  scrollbarWidth: 'thin'
                }}>
                  {cardDetail.targetProfile.profilePhotos.map((photo, index) => {
                    const isSelected = index === selectedImageIndex;
                    return (
                      <Box
                        key={photo.id}
                        sx={{
                          flex: '0 0 auto',
                          width: { xs: 80, sm: 100 },
                          height: { xs: 80, sm: 100 },
                          borderRadius: 2,
                          overflow: 'hidden',
                          border: '3px solid',
                          borderColor: isSelected ? '#C9A86C' : 'transparent',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          opacity: isSelected ? 1 : 0.7,
                          transform: isSelected ? 'scale(1.05)' : 'scale(1)',
                          '&:hover': {
                            opacity: 1,
                            transform: 'scale(1.05)'
                          }
                        }}
                        onClick={() => setSelectedImageIndex(index)}
                      >
                        <Box
                          component="img"
                          src={photo.endpointUrl}
                          alt={`${cardDetail.targetProfile.name} 프로필 ${index + 1}`}
                          sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                          }}
                        />
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>

            {/* 프로필 정보 */}
            <Box sx={{ p: { xs: 2, sm: 3 } }}>
              <Stack spacing={3}>
                {/* 기본 정보 카드 */}
                <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid #E4E1D9' }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, display: 'flex', alignItems: 'center' }}>
                    <Person sx={{ mr: 1, color: 'primary.main' }} />
                    기본 정보
                  </Typography>
                  
                  <Stack spacing={1.5}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" color="text.secondary">나이</Typography>
                      <Typography variant="body1" fontWeight={600}>{cardDetail.targetProfile.age}세</Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" color="text.secondary">성별</Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {cardDetail.targetProfile.gender === 'MALE' ? '남성' : '여성'}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" color="text.secondary">거주지</Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {cardDetail.targetProfile.residenceRegion.level1Name} {cardDetail.targetProfile.residenceRegion.level2Name}
                      </Typography>
                    </Box>

                    {cardDetail.targetProfile.jobTitle && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary">직업</Typography>
                        <Typography variant="body1" fontWeight={600}>{cardDetail.targetProfile.jobTitle}</Typography>
                      </Box>
                    )}

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" color="text.secondary">선호 연령대</Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {cardDetail.targetProfile.preferredAge === 'YOUNGER' ? '연하' :
                         cardDetail.targetProfile.preferredAge === 'OLDER' ? '연상' : '상관없음'}
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>

                {/* 한줄소개 */}
                {cardDetail.targetProfile.oneLiner && (
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5, display: 'flex', alignItems: 'center' }}>
                      <ChatBubbleOutlineIcon sx={{ mr: 1, color: 'secondary.main' }} />
                      한줄소개
                    </Typography>
                    <Paper elevation={0} sx={{ bgcolor: '#F5F7FA', p: 2.5, borderRadius: 2, borderLeft: '4px solid #C9A86C' }}>
                      <Typography variant="body1" sx={{ lineHeight: 1.6, fontStyle: 'italic' }}>
                        "{cardDetail.targetProfile.oneLiner}"
                      </Typography>
                    </Paper>
                  </Box>
                )}

                {/* 자기소개 */}
                {cardDetail.targetProfile.selfIntro && (
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
                      자기소개
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.7, color: 'text.primary' }}>
                      {cardDetail.targetProfile.selfIntro}
                    </Typography>
                  </Box>
                )}

                {/* 관심사 */}
                {cardDetail.targetProfile.interests && cardDetail.targetProfile.interests.length > 0 && (
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5, display: 'flex', alignItems: 'center' }}>
                      <Favorite sx={{ mr: 1, color: 'error.main' }} />
                      관심사
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {cardDetail.targetProfile.interests.map((interest) => (
                        <Chip
                          key={interest.id}
                          label={interest.name}
                          variant="outlined"
                          sx={{
                            fontWeight: 600,
                            borderColor: '#C9A86C',
                            color: '#0D1F3C',
                            fontSize: '0.9rem'
                          }}
                        />
                      ))}
                    </Stack>
                  </Box>
                )}

                {/* 선호 지역 */}
                {cardDetail.targetProfile.preferredRegions && cardDetail.targetProfile.preferredRegions.length > 0 && (
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5, display: 'flex', alignItems: 'center' }}>
                      <LocationOn sx={{ mr: 1, color: 'success.main' }} />
                      선호 지역
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {cardDetail.targetProfile.preferredRegions.map((region, index) => (
                        <Chip
                          key={`${region.level1Id}-${region.level2Id}-${index}`}
                          label={`${region.level1Name} ${region.level2Name}`}
                          variant="outlined"
                          sx={{ 
                            fontWeight: 600,
                            borderColor: 'success.main',
                            color: 'success.main',
                            '&:hover': { bgcolor: 'success.50' }
                          }}
                        />
                      ))}
                    </Stack>
                  </Box>
                )}
              </Stack>
            </Box>
          </Box>
        ) : (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="error" variant="h6" sx={{ mb: 2 }}>
              {detailError}
            </Typography>
            <Button 
              variant="outlined" 
              onClick={() => {
                setProfileModal(false);
                setDetailError(null);
                setCardDetail(null);
              }}
            >
              닫기
            </Button>
          </Box>
        )}
      </DialogContent>
      
      {/* 액션 버튼 (하단 고정) */}
      {cardDetail && (
        <Box sx={{
          p: { xs: 2, sm: 3 },
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          flexShrink: 0
        }}>
          <Stack
            direction="row"
            spacing={1.5}
            sx={{ width: '100%', alignItems: 'center' }}
          >
            <Button
              onClick={() => {
                setProfileModal(false);
                setCardDetail(null);
                setDetailError(null);
                setSelectedImageIndex(0);
              }}
              variant="outlined"
              sx={{
                borderRadius: 2,
                py: 1.5,
                fontWeight: 700,
                order: 1,
                width: 'auto'
              }}
            >
              닫기
            </Button>
            <IconButton
              onClick={(e) => setReportMenuAnchor(e.currentTarget)}
              sx={{
                order: 3,
                color: 'text.secondary',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                width: 44,
                height: 44,
                flexShrink: 0,
              }}
            >
              <MoreVert />
            </IconButton>
            <Menu
              anchorEl={reportMenuAnchor}
              open={Boolean(reportMenuAnchor)}
              onClose={() => setReportMenuAnchor(null)}
              anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
              transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
              <MenuItem
                onClick={() => {
                  setReportMenuAnchor(null);
                  setProfileModal(false);
                  setCardDetail(null);
                  setDetailError(null);
                  setSelectedImageIndex(0);
                  const qs = new URLSearchParams();
                  if (cardDetail?.targetUserId) qs.append('targetId', String(cardDetail.targetUserId));
                  qs.append('cardId', String(card.cardId));
                  navigate(`/report?${qs.toString()}`);
                }}
                sx={{ color: 'error.main' }}
              >
                <ListItemIcon><Flag fontSize="small" color="error" /></ListItemIcon>
                <ListItemText>신고하기</ListItemText>
              </MenuItem>
            </Menu>
            {displayStatus === "EXPIRED_EXTENDABLE" ? (
              <Button
                onClick={() => {
                  setProfileModal(false);
                  setCardDetail(null);
                  setDetailError(null);
                  setSelectedImageIndex(0);
                  handleExtendClick();
                }}
                variant="contained"
                startIcon={<AccessTimeIcon />}
                sx={{
                  borderRadius: 2,
                  py: 1.5,
                  fontWeight: 700,
                  flexGrow: 1,
                  order: 2,
                  bgcolor: '#FF6B35',
                  '&:hover': { bgcolor: '#E55A2B' }
                }}
              >
                연장하기
              </Button>
            ) : (
              <Button
                onClick={() => {
                  setProfileModal(false);
                  setCardDetail(null);
                  setDetailError(null);
                  setSelectedImageIndex(0);
                  onChat(card);
                }}
                variant="contained"
                startIcon={<SendIcon />}
                sx={{
                  borderRadius: 2,
                  py: 1.5,
                  fontWeight: 700,
                  flexGrow: 1,
                  order: 2,
                  bgcolor: '#0D1F3C',
                  '&:hover': { bgcolor: '#1A3461' }
                }}
              >
                대화 시작하기
              </Button>
            )}
          </Stack>
        </Box>
      )}
    </Dialog>
    </>
  );
});